/**
 * Gadget Marvels global scroll reveal (see snippets/gm-scroll-reveal.liquid for the settings).
 *
 * Runs once after parsing. For every section inside <main> it picks reveal targets automatically:
 *   - the content level of the section (the first wrapper with 2+ visible children)
 *   - a short group of text/blocks inside it is split so headline > subline > button follow each other
 *   - lists and grids of similar items (cards, benefits, steps, comparison rows, FAQ rows) stagger
 *   - carousels/sliders are revealed as one unit, their slides and transforms are never touched
 *   - large single images/videos get the "media" variant (fade + tiny scale)
 * Targets already on screen at start stay as they are (no blank first view). Each target reveals
 * once, then the attribute is removed so the element is back to its own styling.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.classList.contains('gm-reveal-on')) return;

  var MAX_STAGGER_STEPS = 6; // delays are capped so nobody waits for content
  var SKIP_SECTION =
    'product-form-component, media-gallery, .product-information, featured-product-information, ' +
    'quick-add-component, [data-gm-no-reveal]';
  var CAROUSEL =
    'slideshow-component, slideshow-container, slideshow-slides, scroll-container, marquee-component, ' +
    '.force-full-width, .carousel-content, [class*="__carousel"], [class*="slider"], [class*="swiper"], [class*="marquee"]';
  var IGNORE_TAGS = { SCRIPT: 1, STYLE: 1, TEMPLATE: 1, LINK: 1, META: 1, NOSCRIPT: 1, BR: 1 };

  var targets = []; // document order
  var revealedUpTo = -1;

  function visibleChildren(el) {
    var out = [];
    if (el instanceof SVGElement) return out; // icons are one unit, never split into paths
    for (var i = 0; i < el.children.length; i += 1) {
      var c = el.children[i];
      if (IGNORE_TAGS[c.tagName]) continue;
      if (c.classList.contains('section-background')) continue;
      if (c.hasAttribute('data-gm-no-reveal')) continue;
      if (c.offsetWidth === 0 && c.offsetHeight === 0) continue;
      var pos = getComputedStyle(c).position;
      if (pos === 'fixed' || pos === 'absolute' || pos === 'sticky') continue;
      out.push(c);
    }
    return out;
  }

  // The element is a carousel, or only wraps one (through single-child wrappers).
  function isCarousel(el, depth) {
    if (el.matches(CAROUSEL)) return true;
    if ((depth || 0) > 3) return false;
    var kids = visibleChildren(el);
    return kids.length === 1 && isCarousel(kids[0], (depth || 0) + 1);
  }

  function isMedia(el) {
    if (el.matches('img, video, picture')) return true;
    if (el instanceof SVGElement) return false;
    var kids = visibleChildren(el);
    return kids.length === 1 && kids[0].matches('img, video, picture, deferred-media');
  }

  // A list/grid of similar items: same tag and mostly the same first class.
  function isRepeatList(kids) {
    if (kids.length < 2) return false;
    var tag = kids[0].tagName;
    var cls = kids[0].classList[0] || '';
    var same = 0;
    for (var i = 0; i < kids.length; i += 1) {
      if (kids[i].tagName === tag && (kids[i].classList[0] || '') === cls) same += 1;
    }
    return same >= Math.max(2, Math.ceil(kids.length * 0.75));
  }

  function add(list, el, order, kind) {
    list.push({ el: el, order: order, kind: kind });
  }

  // Collect targets for one content item (one level of splitting at most).
  function collectItem(item, list, order) {
    if (isCarousel(item)) {
      add(list, item, order, 'base');
      return order + 1;
    }
    if (isMedia(item)) {
      add(list, item, order, 'media');
      return order + 1;
    }
    var kids = visibleChildren(item);
    // Tall single-child wrapper (e.g. a product description): look inside it.
    if (kids.length === 1 && item.offsetHeight > window.innerHeight && !isCarousel(kids[0]) && !isMedia(kids[0]) && visibleChildren(kids[0]).length >= 1) {
      return collectItem(kids[0], list, order);
    }
    if (kids.length > 12) {
      // Long text or long lists (FAQ, policies, descriptions): each block reveals as it enters,
      // without stagger.
      for (var t = 0; t < kids.length; t += 1) add(list, kids[t], 0, isMedia(kids[t]) ? 'media' : 'base');
      return order + 1;
    }
    if (kids.length >= 2 && kids.length <= 12 && !item.matches('table, ul.list-unstyled li')) {
      if (isRepeatList(kids)) {
        for (var i = 0; i < kids.length; i += 1) add(list, kids[i], order + i, isMedia(kids[i]) ? 'media' : 'base');
        return order + kids.length;
      }
      if (kids.length <= 6) {
        for (var j = 0; j < kids.length; j += 1) {
          var k = kids[j];
          var grand = visibleChildren(k);
          if (!isCarousel(k) && grand.length >= 2 && isRepeatList(grand) && grand.length <= 12) {
            for (var g = 0; g < grand.length; g += 1) add(list, grand[g], order + g, isMedia(grand[g]) ? 'media' : 'base');
            order += grand.length;
          } else {
            add(list, k, order, isCarousel(k) ? 'base' : isMedia(k) ? 'media' : 'base');
            order += 1;
          }
        }
        return order;
      }
    }
    add(list, item, order, 'base');
    return order + 1;
  }

  function collectSection(section) {
    if (section.matches(SKIP_SECTION) || section.querySelector(SKIP_SECTION)) return [];
    // Descend through single-child wrappers to the content level.
    var node = section;
    var kids = visibleChildren(node);
    var guard = 0;
    while (kids.length === 1 && !isCarousel(kids[0]) && !isMedia(kids[0]) && guard < 6) {
      node = kids[0];
      kids = visibleChildren(node);
      guard += 1;
    }
    var list = [];
    if (!kids.length) {
      if (node !== section) add(list, node, 0, 'base');
      return list;
    }
    var order = 0;
    for (var i = 0; i < kids.length; i += 1) order = collectItem(kids[i], list, order);
    return list;
  }

  function reveal(index) {
    var t = targets[index];
    if (!t || t.done) return;
    t.done = true;
    if (observer) observer.unobserve(t.el);
    t.el.classList.add('gm-revealed');
    var delay = parseFloat(t.el.style.getPropertyValue('--gm-reveal-delay')) || 0;
    var cleanup = function () {
      t.el.removeAttribute('data-gm-reveal');
      t.el.classList.remove('gm-revealed');
      t.el.style.removeProperty('--gm-reveal-delay');
    };
    // Hand the element back to its own styling once the transition is over.
    setTimeout(cleanup, delay + durationMs + 120);
  }

  function revealThrough(index) {
    // Revealing one target also reveals every earlier target, so a fast scroll or an anchor jump
    // never leaves content hidden above the viewport.
    for (var i = revealedUpTo + 1; i <= index; i += 1) reveal(i);
    if (index > revealedUpTo) revealedUpTo = index;
  }

  var durationMs = parseFloat(getComputedStyle(root).getPropertyValue('--gm-reveal-duration')) || 600;
  var stagger = parseFloat(getComputedStyle(root).getPropertyValue('--gm-reveal-stagger')) || 75;
  var observer = null;

  function init() {
    var main = document.querySelector('main');
    if (!main) return;
    var sections = main.querySelectorAll(':scope > .shopify-section');
    var picked = [];
    for (var s = 0; s < sections.length; s += 1) {
      var list = collectSection(sections[s]);
      for (var i = 0; i < list.length; i += 1) picked.push(list[i]);
    }

    // Read phase: positions and own transforms.
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var reads = picked.map(function (p) {
      var r = p.el.getBoundingClientRect();
      return { top: r.top, bottom: r.bottom, ownTransform: getComputedStyle(p.el).transform !== 'none' };
    });

    // Write phase: tag only what is still below the fold.
    for (var n = 0; n < picked.length; n += 1) {
      var p = picked[n];
      var r = reads[n];
      if (r.top < vh * 0.92) continue; // on screen at start: leave it alone
      var kind = r.ownTransform ? 'fade' : p.kind;
      p.el.setAttribute('data-gm-reveal', kind);
      var step = Math.min(p.order, MAX_STAGGER_STEPS);
      if (step) p.el.style.setProperty('--gm-reveal-delay', step * stagger + 'ms');
      targets.push({ el: p.el, done: false });
    }
    if (!targets.length) return;

    observer = new IntersectionObserver(
      function (entries) {
        for (var e = 0; e < entries.length; e += 1) {
          if (!entries[e].isIntersecting) continue;
          var idx = indexOf(entries[e].target);
          if (idx > -1) revealThrough(idx);
        }
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0 }
    );
    for (var m = 0; m < targets.length; m += 1) observer.observe(targets[m].el);
  }

  function indexOf(el) {
    for (var i = 0; i < targets.length; i += 1) if (targets[i].el === el) return i;
    return -1;
  }

  function fail() {
    // Never leave content hidden: undo everything.
    root.classList.remove('gm-reveal-on');
    var tagged = document.querySelectorAll('[data-gm-reveal]');
    for (var i = 0; i < tagged.length; i += 1) tagged[i].removeAttribute('data-gm-reveal');
  }

  function start() {
    try {
      init();
    } catch (err) {
      fail();
    }
  }

  // Returning from the back/forward cache keeps the revealed state; a full reload starts fresh.
  // Before the page goes into the back/forward cache, show everything, so coming back never shows
  // half-hidden content.
  window.addEventListener('pagehide', function () {
    if (targets.length) revealThrough(targets.length - 1);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
